import { encode } from 'quoted-printable';
import { VCard, NameValue, AdrValue, GeoValue, ImppValue, ValueMetadata } from "../../../types/ParsedVCardTypes";
import { ANNIVERSARY, CLIENTPIDMAP,  GENDER, RELATED, MEMBER, LANGUAGE, BIRTHPLACE, DEATHDATE, DEATHPLACE, EXPERTISE, HOBBY, INTEREST, KIND, XML, ORGDIRECTORY } from '../constants-vcard4';
import * as Constants from '../constants-vcard3';

/**
 * VCard 3.0 Exporter - Optimized for older Outlook versions
 *
 * This exporter generates VCard 3.0 format specifically for compatibility with
 * older Outlook clients (pre-2016) that have limited UTF-8 support.
 *
 * For modern clients (Outlook 2016+, iOS, macOS), use VCard 4.0 format instead,
 * which provides better Unicode support and follows the RFC 6350 standard.
 *
 * Encoding Strategy:
 * - Uses ISO-8859-1 charset (Latin-1) for maximum compatibility with old Outlook
 * - Applies quoted-printable encoding to handle special characters
 * - Note: ISO-8859-1 covers Western European characters (ä, ö, ü, ß, etc.)
 */

// Type for card entry values that can be processed
interface CardEntry extends ValueMetadata<any> {
    value: any;
    metadata?: Record<string, any>;
}

/**
 * Convert a Unicode string to Windows-1252 byte string
 * Windows-1252 is a superset of ISO-8859-1 with printable characters in the 0x80-0x9F range
 * @param str - The input Unicode string
 * @returns A string where each character represents a Windows-1252 byte value
 */
function toWindows1252(str: string): string {
    let result = '';
    for (let i = 0; i < str.length; i++) {
        const code = str.charCodeAt(i);

        // Characters 0x00-0x7F and 0xA0-0xFF are the same in Unicode and Windows-1252
        if (code <= 0x7F || (code >= 0xA0 && code <= 0xFF)) {
            result += String.fromCharCode(code);
        }
        // Special Windows-1252 characters in the 0x80-0x9F range
        else if (code === 0x20AC) result += String.fromCharCode(0x80); // €
        else if (code === 0x201A) result += String.fromCharCode(0x82); // ‚
        else if (code === 0x0192) result += String.fromCharCode(0x83); // ƒ
        else if (code === 0x201E) result += String.fromCharCode(0x84); // „
        else if (code === 0x2026) result += String.fromCharCode(0x85); // …
        else if (code === 0x2020) result += String.fromCharCode(0x86); // †
        else if (code === 0x2021) result += String.fromCharCode(0x87); // ‡
        else if (code === 0x02C6) result += String.fromCharCode(0x88); // ˆ
        else if (code === 0x2030) result += String.fromCharCode(0x89); // ‰
        else if (code === 0x0160) result += String.fromCharCode(0x8A); // Š
        else if (code === 0x2039) result += String.fromCharCode(0x8B); // ‹
        else if (code === 0x0152) result += String.fromCharCode(0x8C); // Œ
        else if (code === 0x017D) result += String.fromCharCode(0x8E); // Ž
        else if (code === 0x2018) result += String.fromCharCode(0x91); // '
        else if (code === 0x2019) result += String.fromCharCode(0x92); // '
        else if (code === 0x201C) result += String.fromCharCode(0x93); // "
        else if (code === 0x201D) result += String.fromCharCode(0x94); // "
        else if (code === 0x2022) result += String.fromCharCode(0x95); // •
        else if (code === 0x2013) result += String.fromCharCode(0x96); // –
        else if (code === 0x2014) result += String.fromCharCode(0x97); // —
        else if (code === 0x02DC) result += String.fromCharCode(0x98); // ˜
        else if (code === 0x2122) result += String.fromCharCode(0x99); // ™
        else if (code === 0x0161) result += String.fromCharCode(0x9A); // š
        else if (code === 0x203A) result += String.fromCharCode(0x9B); // ›
        else if (code === 0x0153) result += String.fromCharCode(0x9C); // œ
        else if (code === 0x017E) result += String.fromCharCode(0x9E); // ž
        else if (code === 0x0178) result += String.fromCharCode(0x9F); // Ÿ
        else {
            // Character not representable in Windows-1252, use '?'
            console.warn(`Character U+${code.toString(16).toUpperCase()} not representable in Windows-1252`);
            result += '?';
        }
    }
    return result;
}

/**
 * Encodes a string to Windows-1252 and then applies quoted-printable encoding
 * @param str - The input string to encode
 * @returns The quoted-printable encoded string for Windows-1252 charset
 */
function encodeWindows1252QuotedPrintable(str: string): string {
    // Convert to Windows-1252 byte string
    const windows1252String = toWindows1252(str);
    // Apply quoted-printable encoding
    return encode(windows1252String);
}

export function generateVCard3(vCardDataArray: VCard[]): string {
    let vCardsString = '';

    vCardDataArray.forEach((vCardData: VCard) => {
        vCardsString += generateSingleVCard3(vCardData);
    });

    return vCardsString;
}

function generateSingleVCard3(vCardData: VCard): string {
    let vCardString = `${Constants.BEGIN_VCARD}\r\n${Constants.VERSION_PROP}:3.0\r\n`; // Change version to 3.0

    for (const prop in vCardData) {
        if (vCardData.hasOwnProperty(prop) && prop !== 'version' && vCardData[prop]) {
            let item = vCardData[prop];

            if (Array.isArray(item)) {
                item.forEach((subItem) => {
                    vCardString += handleValueAndMetadata3(prop, subItem); // Add '3' to function names
                });
            }
            else if (typeof item.value !== 'undefined') {
                vCardString += handleValueAndMetadata3(prop, item); // Add '3' to function names
            }
            else {
                vCardString += `${prop.toUpperCase()}:${item}\r\n`;
            }
        }
    }

    vCardString += `${Constants.END_VCARD}\r\n`;
    return vCardString;
}

function handleValueAndMetadata3(prop: string, item: CardEntry): string | null {
    let val;

    // Add a check here to ignore properties that are not part of VCard 3.0
    const ignoredProps = [
        ANNIVERSARY, BIRTHPLACE, CLIENTPIDMAP, DEATHDATE,
        DEATHPLACE, EXPERTISE, GENDER, HOBBY, INTEREST,
        KIND, LANGUAGE, XML, MEMBER, ORGDIRECTORY, RELATED
    ];

    if (ignoredProps.includes(prop)) {
        return '';
    }

    switch (prop) {
        case Constants.N:
            val = getValueFromName3(item.value);
            break;
        case Constants.ADR:
            val = getValueFromAdr3(item.value);
            break;
        case Constants.TEL:
            val = item.value; // As per vCard 3.0 it's a single text value
            break;
        case Constants.EMAIL:
            val = item.value;
            break;
        case Constants.ORG:
            if (typeof item.value === 'object' && item.value !== null) {
                val = [item.value.organisation, item.value.department].join(',');
            } else if (Array.isArray(item.value)) {
                val = item.value.join(',');
            } else {
                val = item.value;
            }
            break;
        case Constants.GEO:
            val = getValueFromGeo3(item.value);
            break;
        case Constants.TITLE:
            val = item.value;
            break;
        case Constants.ROLE:
            val = item.value;
            break;
        case Constants.LOGO:
            val = item.value; // This could be a URI or inline Base64
            break;
        case Constants.FN:
            val = item.value;
            break;
        case Constants.NICKNAME:
            val = Array.isArray(item.value) ? item.value.join(',') : item.value;
            break;
        case Constants.PHOTO:
            val = item.value; // This could be a URI or inline Base64
            break;
        case Constants.BDAY:
            val = item.value;
            break;
        case Constants.LABEL:
            val = item.value;
            break;
        case Constants.MAILER:
            val = item.value;
            break;
        case Constants.TZ:
            val = item.value;
            break;
        case Constants.REV:
            val = item.value;
            break;
        case Constants.UID:
            val = item.value;
            break;
        case Constants.URL:
            val = item.value;
            break;
        case Constants.CLASS:
            val = item.value;
            break;
        case Constants.KEY:
            val = item.value; // This may require special handling if it is a binary key
            break;
        case Constants.NOTE:
            val = item.value;
            break;
        case Constants.PRODID:
            val = item.value;
            break;
        default:
            val = Array.isArray(item.value) ? item.value.join(',') : item.value;
    }

    let res = `${prop.toUpperCase()}`;
    // Use UTF-8 for better compatibility with modern Outlook
    res += `;CHARSET=UTF-8`;

    for (let attr in item) {
        if(attr !== 'value' && attr !== 'charset' && attr !== 'encoding' && attr !== 'language') {
            const attributeValue = Array.isArray(item[attr]) ? item[attr].join(',') : item[attr];
            res += `;${attr.toUpperCase()}=${attributeValue}`;
        }
    }

    // Keep original UTF-8 string
    res += `:${String(val)}\r\n`;
    return res;
}

// Function for handling 'N' name property as per VCard 3.0
function getValueFromName3(value: NameValue): string {
    return [
        value.familyName[0] || '',
        value.givenName[0] || '',
        value.additionalNames[0] || '',
        value.prefixes[0] || '',
        value.suffixes[0] || ''
    ].join(';');
}

// Function for handling 'ADR' property as per VCard 3.0
function getValueFromAdr3(value: AdrValue): string {
    return [
        value.postOfficeBox[0] || '',
        value.extendedAddress[0] || '',
        value.street[0] || '',
        value.locality[0] || '',
        value.region[0] || '',
        value.postalCode[0] || '',
        value.country[0] || ''
    ].join(';');
}

// Function for handling 'GEO' property as per VCard 3.0
function getValueFromGeo3(value: GeoValue): string {
    return value.latitude + ';' + value.longitude;
}

// Function to handle keys that are not explicitly handled
function getDefaultValue3(key: string, value: any): string {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.join(';');
    return String(value);
}